// Header Bar
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import cubeicon from "../../assets/cubeicon.png";
import kai from "../../assets/kai3.png";
import userAvatars from "../../assets/userAvatars";
import { useNavigate } from "react-router-dom";
import { WorkspaceSelector } from "./sub_comp/WorkspaceSelector";
import { useWorkspace } from "../../context/WorkspaceContext";

export const HeaderBar = ({ email, API_BASE }) => {
  const navigate = useNavigate();
  const [isResetting, setIsResetting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const hasAutoRefreshedRef = useRef(false);
  const { workspace, workspaces, setWorkspace } = useWorkspace();

  // Resolve avatar based on email mapping
  const avatarSrc = userAvatars[email] || userAvatars["default"];
  console.log("apiBase", API_BASE);
  console.log("email", email);
  const currentWorkspace = useMemo(
    () => ({
      id: workspace?.clientId || "default",
      name:
        workspace?.clientName ||
        workspace?.clientId ||
        "default",
    }),
    [workspace]
  );

  const workspaceOptions = useMemo(() => {
    if (Array.isArray(workspaces) && workspaces.length > 0) {
      return workspaces;
    }
    return [currentWorkspace];
  }, [workspaces, currentWorkspace]);

  const handleWorkspaceSelect = useCallback(
    (option) => {
      if (!option?.id) return;
      setWorkspace({
        clientId: option.id,
        sessionId: "default",
        sessionDisplayId: "default",
        clientName: option.name,
      });

      if (typeof window !== "undefined") {
        const isOnOrchestrator = window.location.pathname.includes(
          "/procure_assist/orchestrator"
        );
        if (isOnOrchestrator) {
          window.location.reload();
        } else {
          navigate("/procure_assist/orchestrator");
        }
      } else {
        navigate("/procure_assist/orchestrator");
      }
    },
    [setWorkspace, navigate]
  );
  const handleCreateWorkspaceNav = useCallback(() => {
    navigate("/procure_assist/selectFile");
  }, [navigate]);

  const handleResetMemory = useCallback(
    async (e, { silent = false } = {}) => {
      e?.stopPropagation?.();

      const clientId = workspace?.clientId;
      const sessionId = workspace?.sessionId;

      if (!clientId || !sessionId) {
        if (!silent) {
          alert("Please select a client and session before refreshing memory.");
        }
        return false;
      }

      setIsResetting(true);
      console.log(
        `[reset] starting for client=${clientId} session=${sessionId}`
      );
      try {
        const url =
          `${API_BASE}/orc/refresh_user_session` +
          `?email=${encodeURIComponent(email)}` +
          `&client_id=${encodeURIComponent(clientId)}` +
          `&session_id=${encodeURIComponent(sessionId)}`;

        const response = await fetch(url, {
          method: "GET",
        });

        if (response.ok) {
          if (!silent) {
            setShowSuccessMessage(true);
            // Hide success message after 3 seconds
            setTimeout(() => {
              setShowSuccessMessage(false);
            }, 3000);
          }
          return true;
        }

        console.error("Failed to reset memory");
        if (!silent) {
          alert("Failed to reset memory.");
        }
        return false;
      } catch (error) {
        console.error("Error resetting memory:", error);
        if (!silent) {
          alert("Error resetting memory.");
        }
        return false;
      } finally {
        setIsResetting(false);
      }
    },
    [API_BASE, email, workspace]
  );

  useEffect(() => {
    if (!API_BASE || !email) return;
    if (hasAutoRefreshedRef.current) return;

    const KEY = `kai:sessionRefreshed:${email}`;

    try {
      const alreadyRefreshed = sessionStorage.getItem(KEY);
      if (alreadyRefreshed) {
        hasAutoRefreshedRef.current = true;
        return;
      }

      const clientId = workspace?.clientId;
      const sessionId = workspace?.sessionId;
      if (!clientId || !sessionId) return;

      hasAutoRefreshedRef.current = true;
      (async () => {
        const ok = await handleResetMemory(undefined, { silent: true });
        if (ok) {
          sessionStorage.setItem(KEY, "1");
        } else {
          hasAutoRefreshedRef.current = false;
        }
      })();
    } catch (err) {
      console.warn("Auto refresh skipped:", err);
    }
  }, [API_BASE, email, handleResetMemory, workspace]);

  const handleWorkspaceReset = useCallback(() => {
    handleResetMemory(undefined);
  }, [handleResetMemory]);

  return (
    <>
      <header style={styles.header}>
        <div style={styles.container}>
          {/* Left side - Logo + Title */}
          <div style={styles.brand}>
            <img src={kai} alt="Logo" style={styles.cubeIcon} />
            <span style={styles.title}>Procurement Assistant</span>
          </div>

          <div style={styles.actions}>
            <div style={styles.workspaceWrap}>
              <WorkspaceSelector
                workspaces={workspaceOptions}
                currentWorkspace={currentWorkspace}
                onSelect={handleWorkspaceSelect}
                onCreate={handleCreateWorkspaceNav}
                onReset={handleWorkspaceReset}
                resetBusy={isResetting}
              />
            </div>

            {/* Right side - User avatar with dropdown */}
            <div style={styles.userSection}>
              <div style={styles.userAvatar}>
                <img
                  src={avatarSrc}
                  alt="User Avatar"
                  style={styles.avatarImage}
                  onError={(e) => {
                    e.target.src =
                      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Success Message Toast */}
      {showSuccessMessage && (
        <div style={styles.successToast}>
          <div style={styles.toastContent}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              style={styles.successIcon}
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
            <span>Memory has been cleared</span>
          </div>
        </div>
      )}

    </>
  );
};

const styles = {
  header: {
    background: "#111111",
    borderBottom: "1px solid #333",
    padding: "0 24px",
    position: "sticky",
    top: 0,
    height: "60px",
    zIndex: 2000,
    width: "100%",
    display: "flex",
    alignItems: "center",
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    height: "100%",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  workspaceWrap: {
    position: "relative",
    zIndex: 2500,
  },
  brandDivider: {
    width: "1px",
    height: "22px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))",
    margin: "0 4px 0 2px",
  },
  crumbWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#bdbdbd",
  },
  crumbChevron: {
    width: 16,
    height: 16,
    opacity: 0.6,
  },
  folderCrumb: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 6px",
    borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.12)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(0,0,0,0.15))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
    height: "40px",
  },
  folderButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "28px",
    padding: "0 14px",
    borderRadius: "8px",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.2))",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#f5f5f5",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: "none",
    boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
    transition: "transform .15s ease, box-shadow .2s ease",
  },
  folderButtonInteractive: {
    cursor: "pointer",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(10,10,10,0.35))",
  },
  folderButtonStatic: {
    cursor: "default",
    opacity: 0.98,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.12), rgba(10,10,10,0.45))",
    border: "1px solid rgba(255,255,255,0.16)",
    boxShadow:
      "0 3px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.12)",
  },
  folderDivider: {
    color: "rgba(255,255,255,0.3)",
    fontWeight: 700,
    fontSize: "16px",
    padding: "0 2px",
  },
  workspaceIcon: {
    strokeWidth: 2,
    opacity: 0.9,
  },
  folderIcon: {
    strokeWidth: 2,
    opacity: 0.85,
  },
  cubeIcon: {
    width: 45,
    height: 45,
  },
  title: {
    fontSize: "18px",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    fontWeight: 600,
    lineHeight: "1.2",
    letterSpacing: "0.5px",
    background:
      "linear-gradient(135deg, #2c2c2c 0%, #ffffff 45%, #f5f5f5 55%, #2c2c2c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    // color:" #fffff",
  },
  userSection: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  userAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    overflow: "hidden",
    border: "2px solid #333",
    flexShrink: 0,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  successToast: {
    position: "fixed",
    top: "80px",
    right: "24px",
    background: "linear-gradient(135deg, #10b981, #059669)",
    color: "white",
    padding: "16px 20px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(16, 185, 129, 0.3)",
    zIndex: 1001,
    animation: "slideInRight 0.3s ease-out",
  },
  toastContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    fontWeight: 500,
  },
  successIcon: {
    strokeWidth: 2,
    flexShrink: 0,
  },
};

// Add CSS keyframes for animations (you'll need to add this to your CSS file)
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .dropdown-item:hover {
    background-color: #2a2a2a !important;
  }

  .folder-chip.workspace-chip:hover {
    background: linear-gradient(180deg, rgba(255,255,255,0.15), rgba(10,10,10,0.45));
    box-shadow: 0 4px 12px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.1) inset;
    transform: translateY(-1px);
  }

  .folder-chip.workspace-chip:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
  }

  .folder-chip.client-chip {
    color: #f4f4f4;
  }
`;
document.head.appendChild(styleSheet);
